import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Avatar
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

export default function PlatformProfilePage() {
  const { refreshMe } = usePlatformAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState(null);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [fullName, setFullName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch('/api/platform/me/profile');
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load profile');
      const p = json.profile;
      setEmail(p.email || '');
      setFirstName(p.first_name || '');
      setLastName(p.last_name || '');
      setFullName(p.full_name || '');
      setDisplayName(p.display_name || '');
      setMobileNumber(p.mobile_number || '');
      setAvatarUrl(p.avatar_url || '');
      setMfaEnabled(Boolean(p.mfa_enabled));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const res = await platformApiFetch('/api/platform/me/profile', {
        method: 'PATCH',
        body: {
          email: email.trim().toLowerCase(),
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
          full_name: fullName.trim() || null,
          display_name: displayName.trim() || null,
          mobile_number: mobileNumber.trim() || null,
          avatar_url: avatarUrl.trim() || null,
          mfa_enabled: mfaEnabled
        }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      await refreshMe();
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(false);
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match');
      return;
    }
    setPwSaving(true);
    try {
      const res = await platformApiFetch('/api/platform/me/password', {
        method: 'POST',
        body: { current_password: currentPassword, new_password: newPassword }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Password change failed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwSuccess(true);
    } catch (err) {
      setPwError(err.message);
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        My profile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Platform operator account. Your data is never mixed with tenant records.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Profile saved.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, maxWidth: 720 }}>
        <Typography variant="h6" gutterBottom>
          Profile
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar src={avatarUrl || undefined} sx={{ width: 64, height: 64 }} />
          <Typography variant="body2" color="text.secondary">
            Optional avatar URL (https).
          </Typography>
        </Box>
        <Box component="form" onSubmit={saveProfile}>
          <TextField fullWidth required label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} margin="normal" />
          <TextField fullWidth label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} margin="normal" />
          <TextField fullWidth label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} margin="normal" />
          <TextField fullWidth label="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} margin="normal" />
          <TextField fullWidth label="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} margin="normal" />
          <TextField fullWidth label="Phone" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} margin="normal" />
          <TextField fullWidth label="Avatar URL" value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} margin="normal" placeholder="https://..." />
          <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
            MFA is not enforced yet. This toggle only records your preference for a future release.
          </Alert>
          <FormControlLabel
            control={<Switch checked={mfaEnabled} onChange={(e) => setMfaEnabled(e.target.checked)} />}
            label="I want multi-factor authentication when available"
          />
          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={saving || !email.trim()}>
              {saving ? 'Saving…' : 'Save profile'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, maxWidth: 720 }}>
        <Typography variant="h6" gutterBottom>
          Password
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {pwError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPwError(null)}>
            {pwError}
          </Alert>
        )}
        {pwSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPwSuccess(false)}>
            Password updated.
          </Alert>
        )}
        <Box component="form" onSubmit={savePassword}>
          <TextField
            fullWidth
            type="password"
            label="Current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            margin="normal"
            autoComplete="current-password"
          />
          <TextField
            fullWidth
            type="password"
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            autoComplete="new-password"
            helperText="At least 8 characters"
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            autoComplete="new-password"
          />
          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="outlined" disabled={pwSaving || !currentPassword || !newPassword}>
              {pwSaving ? 'Updating…' : 'Change password'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
